import { toast } from 'react-toastify';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@accure/ui';
import { Loader2, Trash2 } from 'lucide-react';

import { DeleteWorkstreamMutation } from '@/project-management/mutations/delete-workstream.mutation';
import { WorkstreamQuery } from '@/project-management/queries/workstream.query';
import { label } from '@/utils/format/label';

import type { DeleteWorkstreamModalOpen } from './delete-workstream.provider';

export type DeleteWorkstreamModalProps = {
  open: DeleteWorkstreamModalOpen;
  onClose: () => void;
};

export function DeleteWorkstreamModal({
  open,
  onClose,
}: DeleteWorkstreamModalProps) {
  return (
    <Dialog open={Boolean(open)} onOpenChange={onClose}>
      {open ? (
        <DeleteWorkstreamModalContent id={open.id} onClose={onClose} />
      ) : null}
    </Dialog>
  );
}

type DeleteWorkstreamModalContentProps = {
  id: string;
  onClose: () => void;
};

function DeleteWorkstreamModalContent({
  id,
  onClose,
}: DeleteWorkstreamModalContentProps) {
  const deleteWorkstream = DeleteWorkstreamMutation.useMutation();
  const workstreamQuery = WorkstreamQuery.useQuery({
    id,
  });

  if (workstreamQuery.isLoading) {
    return (
      <DialogContent className="flex min-h-[450px] items-center justify-center sm:max-w-sm">
        <Loader2 className="size-8 animate-spin text-gray-300" />
      </DialogContent>
    );
  }

  if (workstreamQuery.isError || !workstreamQuery.data) {
    throw workstreamQuery.error;
  }

  const { workstream } = workstreamQuery.data;

  async function handleDeleteWorkstream() {
    await toast.promise(deleteWorkstream.mutateAsync({ id }), {
      pending: 'Deleting workstream...',
      success: 'Workstream deleted successfully.',
      error: 'Failed to delete workstream. Please try again.',
    });
    onClose();
  }

  return (
    <DialogContent className="gap-6 sm:max-w-sm">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2 text-red-600">
          <Trash2 className="size-5" />
          Delete Workstream
        </DialogTitle>
      </DialogHeader>

      <div className="space-y-4">
        <p className="text-gray-700">
          Are you sure you want to delete this workstream? This action cannot be
          undone.
        </p>

        <div className="space-y-3 rounded-lg bg-gray-50 p-4">
          <h4 className="font-medium text-gray-900">{workstream.name}</h4>
          <p className="text-sm text-gray-600">{workstream.description}</p>
          {workstream.tasks.length > 0 ? (
            <div className="space-y-2 border-t pt-3">
              <p className="text-sm font-medium text-gray-700">
                Associated tasks that will be deleted:
              </p>
              <ul className="list-disc space-y-1 pl-3 text-sm text-gray-600">
                {workstream.tasks.slice(0, 3).map((task) => (
                  <li key={task.id}>{task.title}</li>
                ))}
                {workstream.tasks.length > 3 ? (
                  <li className="mt-16 list-none italic text-gray-500">
                    ... and {workstream.tasks.length - 3}{' '}
                    {label(
                      ['more task', 'more tasks'],
                      workstream.tasks.length - 3,
                    )}
                  </li>
                ) : null}
              </ul>
            </div>
          ) : null}
        </div>
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="destructive"
          onClick={handleDeleteWorkstream}
          disabled={deleteWorkstream.isLoading}
        >
          {deleteWorkstream.isLoading ? (
            <>
              <Loader2 className="size-3.5 animate-spin" />
              Deleting...
            </>
          ) : (
            <>Delete Workstream</>
          )}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
